/**
 * FounderHome — the simplified two-tier finance home for the Founder persona.
 *
 * Tier 1 (the 60-second glance): one hero number (cash on hand) + runway in
 * months + three supporting figures (money owed / sales / going out soon) + a
 * plain-language health line, beside "Needs you today" (the reused
 * `PriorityPanel`). Tier 2 (the full numbers) is deferred behind one
 * disclosure: the KPI strip, health signals, and overdue ledger.
 *
 * Fetches its own data so the route stays a thin role/vertical dispatcher. New `briefings.cashRunway` powers the runway line;
 * everything else reuses the existing `today` / `workspaceSignals` / `aging`
 * reads and presentational components.
 *
 * @see prototype/home/dashboard-simple.html — the approved design baseline
 */
import { useState, type ReactNode } from "react";
import { Card, CardBody, cn } from "@flint/ui";
import { Check, ChevronDown, AlertTriangle } from "lucide-react";
import { trpc } from "../../../lib/trpc.ts";
import { paiseToInrShort, paiseToInrLakhWord } from "../../../lib/format.ts";
import { DashboardKpiStrip } from "./kpi-strip.tsx";
import { HealthSignalsRow } from "./health-signals-row.tsx";
import { OverdueCustomersPanel } from "./overdue-customers-panel.tsx";
import { PriorityPanel } from "./priority-panel.tsx";

export function FounderHome() {
  const todayQ = trpc.briefings.today.useQuery({});
  const signalsQ = trpc.briefings.workspaceSignals.useQuery({});
  const agingQ = trpc.reports.aging.useQuery({});
  const runwayQ = trpc.briefings.cashRunway.useQuery({});

  const isLoading =
    todayQ.isLoading || signalsQ.isLoading || agingQ.isLoading || runwayQ.isLoading;
  const firstError = todayQ.error ?? signalsQ.error ?? agingQ.error ?? runwayQ.error;

  if (isLoading) {
    return (
      <Card>
        <CardBody>
          <p className="text-sm text-muted">Loading your business…</p>
        </CardBody>
      </Card>
    );
  }
  if (firstError) {
    return (
      <Card className="border-bad">
        <CardBody>
          <p className="text-bad font-medium">Unable to load your home.</p>
          <p className="text-sm text-muted mt-2">{firstError.message}</p>
        </CardBody>
      </Card>
    );
  }

  const today = todayQ.data;
  const signals = signalsQ.data;
  const aging = agingQ.data;
  const runway = runwayQ.data;
  if (!today || !signals || !aging || !runway) return null;

  const cashOnHandPaise = sumCashBreakdown(signals.cashBreakdown);
  const goingOutPaise = (
    BigInt(signals.tds.depositDuePaise || "0") + BigInt(signals.approvals.totalAtStakePaise || "0")
  ).toString();
  // Healthy unless runway is short. AI narrates; this code decides the colour.
  const healthy = runway.runwayMonths === null || runway.runwayMonths >= 3;

  const overdueRows = aging.byCustomer.filter((r) => r.oldestDays > 0);
  const topOverdue = pickTopOverdue(overdueRows);

  return (
    <div className="space-y-5">
      <header>
        <h1 className="text-2xl font-bold tracking-tight">
          {today.greeting || "Good morning"}.
        </h1>
        <p className="text-muted text-sm mt-1">
          Here&rsquo;s your business in one look. Three things need you today.
        </p>
      </header>

      {/* Glance layer — two columns on wide screens, equal height. */}
      <div className="grid grid-cols-1 xl:grid-cols-[1.6fr_1fr] gap-5 xl:items-stretch">
        <div className="flex flex-col">
          <Card
            className={cn(
              "flex-1 relative overflow-hidden border-l-4",
              healthy ? "border-l-ok" : "border-l-warn",
            )}
          >
            <CardBody className="flex flex-col h-full p-6">
              <p className="text-sm text-muted">Money you have right now</p>
              <p className="text-[clamp(34px,7vw,52px)] font-extrabold tracking-tight leading-none mt-1 tabular-nums">
                {paiseToInrLakhWord(cashOnHandPaise)}
              </p>
              <p className="text-base text-ink-2 mt-3">
                {runway.runwayMonths !== null ? (
                  <>
                    Enough to run for about{" "}
                    <strong>
                      {runway.runwayMonths} month{runway.runwayMonths === 1 ? "" : "s"}
                    </strong>{" "}
                    at your usual spending.
                  </>
                ) : (
                  <>Cash on hand across all your accounts.</>
                )}
              </p>

              {/* Three supporting figures — distinct numbers, not a chart. */}
              <div className="grid grid-cols-3 gap-x-5 gap-y-2 mt-5 pt-[18px] border-t border-line">
                <Figure
                  label="Money owed to you"
                  value={paiseToInrShort(today.receivables.totalPaise)}
                  meta={`${paiseToInrShort(today.receivables.overduePaise)} is overdue`}
                />
                <Figure
                  label="Sales this month"
                  value={paiseToInrShort(today.cash.balancePaise)}
                  meta="received this month"
                />
                <Figure
                  label="Going out soon"
                  value={paiseToInrShort(goingOutPaise)}
                  meta="tax + payments due"
                  warn
                />
              </div>

              <div className="mt-auto pt-4 flex items-center gap-2 text-sm text-ink-2 border-t border-line">
                <span
                  className={cn(
                    "grid place-items-center w-[18px] h-[18px] rounded-full text-paper",
                    healthy ? "bg-ok" : "bg-warn",
                  )}
                >
                  {healthy ? <Check size={11} /> : <AlertTriangle size={11} />}
                </span>
                {healthy
                  ? "Your business looks healthy. No cash-flow worries this week."
                  : "Cash is running tighter than usual — keep an eye on spending this week."}
              </div>
            </CardBody>
          </Card>
        </div>

        <div className="flex flex-col">
          <h2 className="text-base font-bold mb-3">Needs you today</h2>
          <PriorityPanel
            topOverdue={
              topOverdue
                ? {
                    customerName: topOverdue.customerName,
                    outstandingPaise: topOverdue.outstandingPaise,
                    oldestDays: topOverdue.oldestDays,
                    customerId: topOverdue.customerId,
                  }
                : null
            }
            pettyCash={signals.pettyCash}
            bankMatch={signals.bankMatch}
            tds={signals.tds}
            approvals={signals.approvals}
          />
        </div>
      </div>

      <Tier2Disclosure>
        <DashboardKpiStrip
          cashPaise={cashOnHandPaise}
          cashBreakdown={signals.cashBreakdown}
          receivablesTotalPaise={today.receivables.totalPaise}
          overduePaise={today.receivables.overduePaise}
          overdueInvoiceCount={today.receivables.overdueInvoiceCount}
          salesThisMonthPaise={today.cash.balancePaise}
          complianceDeadlineLabel={today.compliance.nextDeadline}
          complianceDaysUntil={today.compliance.daysUntil}
        />
        <HealthSignalsRow
          pettyCash={signals.pettyCash}
          bankMatch={signals.bankMatch}
          tds={signals.tds}
          approvals={signals.approvals}
        />
        <OverdueCustomersPanel
          rows={overdueRows.map((r) => ({
            customerId: r.customerId,
            customerName: r.customerName,
            outstandingPaise: r.outstandingPaise,
            oldestDays: r.oldestDays,
            invoiceCount: r.invoiceCount,
          }))}
          isEmpty={overdueRows.length === 0}
        />
      </Tier2Disclosure>
    </div>
  );
}

// ── pieces ──────────────────────────────────────────────────────────

function Figure(props: { label: string; value: string; meta: string; warn?: boolean }) {
  return (
    <div className="min-w-0">
      <p className="text-xs text-muted mb-1">{props.label}</p>
      <p
        className={cn(
          "text-[clamp(19px,4vw,23px)] font-bold tracking-tight tabular-nums",
          props.warn && "text-warn",
        )}
      >
        {props.value}
      </p>
      <p className="text-xs text-muted mt-1">{props.meta}</p>
    </div>
  );
}

function Tier2Disclosure({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="mt-[18px]">
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className="w-full border border-dashed border-line-2 bg-paper rounded-md py-3 px-4 flex items-center justify-center gap-2 text-sm font-semibold text-sky-700 hover:bg-sky-50"
      >
        {open ? "Hide the full numbers" : "See the full numbers"}
        <ChevronDown size={15} className={cn("transition-transform", open && "rotate-180")} />
      </button>
      {open ? <div className="mt-[18px] space-y-4">{children}</div> : null}
    </div>
  );
}

// ── helpers (mirrors the prior route helpers) ───────────────────────

interface AgingRow {
  customerId: string;
  customerName: string;
  outstandingPaise: string;
  oldestDays: number;
  invoiceCount: number;
}

function pickTopOverdue<T extends AgingRow>(rows: readonly T[]): T | null {
  let best: T | null = null;
  let bestPaise = 0n;
  for (const r of rows) {
    const p = BigInt(r.outstandingPaise || "0");
    if (p > bestPaise) {
      best = r;
      bestPaise = p;
    }
  }
  return best;
}

function sumCashBreakdown(breakdown: {
  bankPaise: string;
  cashPaise: string;
  cardPaise: string;
}): string {
  return (
    BigInt(breakdown.bankPaise || "0") +
    BigInt(breakdown.cashPaise || "0") +
    BigInt(breakdown.cardPaise || "0")
  ).toString();
}
